import moment from 'moment';
import { formatDateTime } from './apiKeys';
import { formatNumber } from './format';
import { formatPoints, formatLoadPercent, loadBadgeClass } from './externalApiCost';

/**
* Rendering helpers for the `externalApi` usage summary the client list now
* carries per client (VD-725 follow-up). The field always exists; for a
* client that never called the external API every value but `used` is null.
*/

export const EXTERNAL_API_AUTH = {
    BASIC: 'basic',
    API_KEY: 'apiKey',
};

/** "Use External API" column: Yes/No badge */
export const getExternalApiUsedBadge = (externalApi, lang) => {
    let used = !!(externalApi && externalApi.used);

    return {
        label: used ? 'Yes'.translate(lang) : 'No'.translate(lang),
        className: used ? 'api-key-badge api-key-badge-active' : 'api-key-badge api-key-badge-revoked',
    };
};

/**
* "External API Auth" column: method of the client's last successful call.
* Basic is flagged orange because it is on its way out; API key is green;
* a client that never called the API shows a dash.
*/
export const getExternalApiAuthBadge = (externalApi, lang) => {
    let method = externalApi ? externalApi.authMethod : null;

    if (method === EXTERNAL_API_AUTH.BASIC) {
        return {
            label: 'Basic'.translate(lang),
            className: 'api-key-badge api-key-badge-grace',
            tooltip: null,
        };
    }

    if (method === EXTERNAL_API_AUTH.API_KEY) {
        return {
            label: 'API key'.translate(lang),
            className: 'api-key-badge api-key-badge-active',
            tooltip: externalApi && externalApi.lastBasicAt
                ? `${'Basic last used'.translate(lang)}: ${formatDateTime(externalApi.lastBasicAt)}`
                : null,
        };
    }

    return {
        label: '–',
        className: 'api-keys-muted',
        tooltip: null,
    };
};

/**
* "Last used" column: relative time with the exact timestamp and IP as a
* tooltip, "Never" for a client that has no successful call on record.
*/
export const getExternalApiLastUsed = (externalApi, lang) => {
    let lastUsedAt = externalApi ? externalApi.lastUsedAt : null;

    if (!lastUsedAt) {
        return { text: 'Never'.translate(lang), tooltip: null };
    }

    let date = moment(lastUsedAt);

    if (!date.isValid()) {
        return { text: String(lastUsedAt), tooltip: null };
    }

    let exact = date.format('YYYY-MM-DD HH:mm');
    let ip = externalApi.lastUsedIp ? externalApi.lastUsedIp : null;

    return {
        text: date.fromNow(),
        tooltip: ip ? `${exact} • ${ip}` : exact,
    };
};

/**
* "Requests (7d)" column: formatted count, with today/30d/avg-per-day/errors-7d
* as a tooltip.
*/
export const getExternalApiRequests7d = (externalApi, lang) => {
    let value = externalApi ? externalApi.requests7d : null;

    let tooltip = [
        `${'Today'.translate(lang)}: ${formatNumber(externalApi ? externalApi.requestsToday : 0)}`,
        `${'30d'.translate(lang)}: ${formatNumber(externalApi ? externalApi.requests30d : 0)}`,
        `${'Avg/day'.translate(lang)}: ${externalApi && externalApi.avgPerDay7d !== null && externalApi.avgPerDay7d !== undefined ? Number(externalApi.avgPerDay7d).toFixed(1) : '0'}`,
        `${'Errors 7d'.translate(lang)}: ${formatNumber(externalApi ? externalApi.errors7d : 0)}`,
    ].join(' • ');

    return {
        text: formatNumber(value),
        tooltip,
    };
};

/** "Heavy user" column: a badge only when the client is flagged heavy, otherwise nothing */
export const getExternalApiHeavyBadge = (externalApi, lang) => {
    if (!externalApi || !externalApi.heavy) {
        return null;
    }

    return {
        label: 'Heavy'.translate(lang),
        className: 'api-key-badge api-key-badge-heavy',
    };
};

/**
* "Points (7d)" column (VD-725 cost model): the real cost of a client's
* traffic - processing time, not request count. Tooltip carries today/30d/
* avg-points-per-request/busy-seconds the same way Requests (7d) does.
*/
export const getExternalApiPoints7d = (externalApi, lang) => {
    let value = externalApi ? externalApi.points7d : null;

    let tooltip = [
        `${'Today'.translate(lang)}: ${formatPoints(externalApi ? externalApi.pointsToday : 0)}`,
        `${'30d'.translate(lang)}: ${formatPoints(externalApi ? externalApi.points30d : 0)}`,
        `${'Avg points/request'.translate(lang)}: ${externalApi && externalApi.avgPointsPerRequest7d !== null && externalApi.avgPointsPerRequest7d !== undefined ? Number(externalApi.avgPointsPerRequest7d).toFixed(1) : '0'}`,
        `${'Busy seconds 7d'.translate(lang)}: ${formatNumber(externalApi ? externalApi.busySeconds7d : 0)}`,
    ].join(' • ');

    return {
        text: formatPoints(value),
        tooltip,
    };
};

/**
* "Peak load (7d)" column: the worst single minute's share of cluster
* capacity in the last 7 days - this, not the request count, is what makes a
* client "heavy" now. Color thresholds come from the stats/insights `cost`
* object when the caller has one; the clients list itself has no `cost`
* object (its own endpoint doesn't return one), so it falls back to the
* documented defaults (25% / 10%) purely for color, the percentage shown is
* always the backend's own number.
*/
export const getExternalApiPeakLoadBadge = (externalApi, lang, cost) => {
    let value = externalApi ? externalApi.peakLoad7d : null;

    return {
        label: formatLoadPercent(value),
        className: loadBadgeClass(value, cost),
        widthPercent: Math.max(0, Math.min(100, Number(value) || 0)),
    };
};
